import React, { useMemo } from 'react';
import { NumberInput, Select } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import type { KlaviyoTestFormValues } from './KlaviyoTestFormTypes';
import type { SelectOption } from './ProductPlanFields';

export type ProgrammeFieldProps = {
	form: UseFormReturnType<KlaviyoTestFormValues>;
	disabled: boolean;
	programmeOptions: SelectOption[];
	onProgrammeIdChange: (value: string | null) => void;
};

export function ProgrammeField(props: ProgrammeFieldProps) {
	const { form, disabled, programmeOptions, onProgrammeIdChange } = props;

	return (
		<Select
			{...form.getInputProps('programmeId')}
			onChange={onProgrammeIdChange}
			label="Programme"
			data={programmeOptions}
			disabled={disabled}
			withAsterisk
			clearable
			searchable
		/>
	);
}

export type AccessProductFieldProps = {
	form: UseFormReturnType<KlaviyoTestFormValues>;
	disabled: boolean;
	productOptions: SelectOption[];
};

export function AccessProductField(props: AccessProductFieldProps) {
	const { form, disabled, productOptions } = props;

	return (
		<Select
			{...form.getInputProps('productId')}
			label="Access product"
			description="The product the learner accesses this programme through"
			data={productOptions}
			disabled={disabled || !form.values.programmeId}
			withAsterisk
			clearable
			searchable
		/>
	);
}

export type EventOccurrenceFieldProps = {
	form: UseFormReturnType<KlaviyoTestFormValues>;
	disabled: boolean;
	eventOccurrenceOptions: SelectOption[];
};

/** Event session picker for the EVENT_REGISTRATION_* metrics. */
export function EventOccurrenceField(props: EventOccurrenceFieldProps) {
	const { form, disabled, eventOccurrenceOptions } = props;

	return (
		<Select
			{...form.getInputProps('eventOccurrenceId')}
			label="Event session"
			data={eventOccurrenceOptions}
			disabled={disabled}
			withAsterisk
			clearable
			searchable
		/>
	);
}

export type RegistrantTimezoneFieldProps = {
	form: UseFormReturnType<KlaviyoTestFormValues>;
	disabled: boolean;
};

export function RegistrantTimezoneField(props: RegistrantTimezoneFieldProps) {
	const { form, disabled } = props;
	const timezoneOptions = useMemo(
		() => Intl.supportedValuesOf('timeZone').map((timezone) => ({ value: timezone, label: timezone })),
		[],
	);

	return (
		<Select
			value={form.values.userTimezone || null}
			onChange={(value) => form.setFieldValue('userTimezone', value ?? '')}
			label="Registrant timezone"
			description="Real registrations use the registrant's browser timezone"
			placeholder="Australia/Brisbane (default)"
			data={timezoneOptions}
			disabled={disabled}
			clearable
			searchable
		/>
	);
}

export type QuizFieldsProps = {
	form: UseFormReturnType<KlaviyoTestFormValues>;
	disabled: boolean;
	quizOptions: SelectOption[];
};

export function QuizFields(props: QuizFieldsProps) {
	const { form, disabled, quizOptions } = props;

	return (
		<>
			<Select
				{...form.getInputProps('quizId')}
				label="Quiz"
				data={quizOptions}
				disabled={disabled}
				withAsterisk
				clearable
				searchable
			/>
			<NumberInput
				{...form.getInputProps('score')}
				label="Score (%)"
				description="Sent as a fraction, like real passes (85% → 0.85)"
				min={0}
				max={100}
				disabled={disabled}
				withAsterisk
			/>
		</>
	);
}
